import { PlusOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import {
  Button,
  Col,
  FormInstance,
  message,
  Row,
  Table,
  Typography,
} from "antd";
import Card from "../components/Card";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import { ExclusionList, FormFieldConfig } from "../helpers/shared-interfaces";
import {
  formatSnakeCase,
  handleValidationErrors,
  request,
} from "../helpers/utilities";
import { CREATE_EXCLUSION_LIST, EXCLUSION_LIST_QUERY } from "../gql/general";
import { PageToolbar } from "../components/PageToolbar";
import { PAGE_SIZE } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";


const { Text } = Typography;

export const ExclusionListUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "name",
      name: "name",
      label: "Name",
      inputType: "string",
      placeholder: "Enter exclusion name",
      rule: "string",
      required: true,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "exclusionListType",
      name: "exclusionListType",
      label: "Exclsuion List Type",
      inputType: "select",
      selectOptions: exclusionListTypeOptions,
      placeholder: "Select Exclusion Type",
      rule: "string",
      required: true,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "statementType",
      name: "statementType",
      label: "Statement Type",
      inputType: "select",
      selectOptions: statementTypeOptionsForExclusionList,
      placeholder: "Select Statement type",
      rule: "string",
      required: true,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "description",
      name: "description",
      label: "Description",
      inputType: "textArea",
      placeholder: "Describe Exclusion List",
      rule: "string",
      required: true,
      elementStyle: {
        width: "100%",
      },
      colConfig: {
        span: 24,
      },
    },
  ];
  return (
    <Row>
      <Col span={24}>
        <BaseForm
          layout="vertical"
          reference={props.reference}
          fieldsToHide={[]}
          fields={fields}
        />
      </Col>
    </Row>
  );
};

interface State {
  exclusionLists: ExclusionList[];
  isLoading: boolean;
  query: string;
  page: number;
  pageSize: number;
  exclusionListsCount: number;
  showExclusionListModal: boolean;
}

export const statementTypeOptionsForExclusionList = [
  { id: "payment_reminder", name: "Payment Reminder" },
  { id: "statements", name: "Statements" },
  { id: "redemptions", name: "Redemptions" },
];

export const exclusionListTypeOptions = [
  { id: "inclusion", name: "Inclusion" },
  { id: "exclusion", name: "Exclusion" }
];

export const columns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "Name",
    width: "50%",
    render(text: string, record: ExclusionList): JSX.Element {
      return (
        <NavLink
          to={`/exclusion-lists/${record.id}`}
          data-cy="test-exclusion-details-link"
        >
          {text}
        </NavLink>
      );
    },
  },
  {
    title: "Exclsuion Type",
    dataIndex: "exclusionListType",
    key: "exclusionListType",
    width: "25%",
    render(text: string): JSX.Element {
      return (
        <Text style={{ textTransform: "capitalize" }}>
          {text}
        </Text>
      );
    },
  },
  {
    title: "Statement Type",
    dataIndex: "statementType",
    key: "statementType",
    width: "25%",
    render(text: string): JSX.Element {
      return (
        <Text style={{ textTransform: "capitalize" }}>
          {formatSnakeCase(text)}
        </Text>
      );
    },
  },
];

export default class ExclusionLists extends React.Component<
  RouteComponentProps,
  State
> {
  exclusionListReference: RefObject<FormInstance>;
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      exclusionLists: [],
      isLoading: true,
      query: "",
      page: 1,
      pageSize: PAGE_SIZE,
      showExclusionListModal: false,
      exclusionListsCount: 0,
    };

    this.exclusionListReference = React.createRef<FormInstance>();
    this.nextPage = this.nextPage.bind(this);
  }

  nextPage(page: number): void {
    this.setState({ page });
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.exclusionListReference = ref;
  };

  setQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    this.setState({ query });
  };

  getExclusionLists = async (): Promise<void> => {
    this.setState({ isLoading: true });
    try {
      const { data: { data } }: any = await request({
        query: EXCLUSION_LIST_QUERY,
      });
      this.setState({
        exclusionLists: data.exclusionLists,
        exclusionListsCount: data.exclusionLists.length,
      });
    } catch (error) {
      message.error(`Failed to load exclusion lists - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  toggleExclusionListModal = () => {
    this.setState((prevState) => ({
      showExclusionListModal: !prevState.showExclusionListModal,
    }));
  };

  createExclusionList = async (): Promise<void> => {
    if (!this.exclusionListReference.current) return;

    const fieldsToBeValidated: string[] = ["name", "statementType", "exclusionListType", "description"];
    let data;
    try {
      data = await this.exclusionListReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }
    this.setState({ isLoading: true });
    try {
      await request({
        query: CREATE_EXCLUSION_LIST,
        variables: { ...data },
      });
      this.toggleExclusionListModal();
      await this.getExclusionLists();
    } catch (error) {
      message.error(`An error occured - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  async componentDidMount(): Promise<void> {
    await this.getExclusionLists();
  }

  render() {
    const { isLoading, exclusionLists, query, page, pageSize, exclusionListsCount } = this.state;

    const tableData = exclusionLists
      .map((data) => {
        return { ...data, key: data.id };
      })
      .filter(
        (data) =>
          data.name.toLowerCase().includes(query) ||
          data.statementType.toLowerCase().includes(query),
      );

    return (
      <>
        <Card>
        <div className="deparments-table-container">
          <PageToolbar modelName="exclusion lists"
            searchPlaceholder="Filter by..."
            onSearchChange={this.setQuery}
            searchValue={query}
            page={page}
            totalCount={exclusionListsCount}
            pageSize={pageSize}
            currentDataCount={exclusionLists.length}
            actions={
              <Button
                className="base-button"
                size="large"
                data-testid="create-exclusion-list"
                onClick={this.toggleExclusionListModal}
              >
                <PlusOutlined />
                Create exclusion list
              </Button>
            }
          />
          <Table
            loading={isLoading}
            columns={columns}
            dataSource={tableData}
            pagination={{
              current: page,
              pageSize,
              total: tableData.length,
              onChange: this.nextPage,
            }}
          />
        </div>
      </Card>
      {this.state.showExclusionListModal &&
        ModalCreator({
          title: "Add Exclusion List",
          okType: "primary",
          okText: "Add Exclusion",
          showModal: this.state.showExclusionListModal,
          isLoading: this.state.isLoading,
          onOkay: this.createExclusionList,
          toggleModal: this.toggleExclusionListModal,
          inscription: ExclusionListUI({
            reference: this.exclusionListReference,
          }),
        })}
      </>
    );
  }
}
